import { useContext, useMemo, useState } from 'react';
import useSessionState from 'hooks/useSessionState';
import { Link } from 'react-router-dom';
import { Button, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';

import { useRecurringDonations } from 'hooks/useDashboard';
import { useCancelSubscription } from 'hooks/useStripePayments';
import AuthContext from 'context/authContext';

import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import SoftBadge from 'components/common/SoftBadge';
import CancellationReasonModal from 'components/common/modals/CancellationReasonModal';
import { FormProvider, RHFSelect } from 'components/common/form';
import paths from 'routes/paths';
import dayjs from 'dayjs';
import { formatMoney } from 'helpers/utils';

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Payment Failed', value: 'past_due' },
  { label: 'Cancelled', value: 'cancelled' }
];

const statusBadgeMap = {
  active: 'success',
  past_due: 'danger',
  cancelled: 'warning'
};

const columns = (onCancel, isCancelling, canManage) => [
  {
    accessor: 'donor_name',
    Header: 'Donor',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { donor } = rowData.row.original;
      if (!donor) return 'Unknown';
      return (
        <Link to={paths.donorDetails.replace(':id', donor.id)}>
          {donor.first_name} {donor.last_name}
        </Link>
      );
    }
  },
  {
    accessor: 'amount',
    Header: 'Amount',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { amount, donor, latest_order_id } = rowData.row.original;
      if (!donor?.id || !latest_order_id) return formatMoney(amount);
      return (
        <Link
          to={`/donors/${donor.id}/giving/${latest_order_id}`}
          className="fw-medium"
          title="View latest payment details"
        >
          {formatMoney(amount)}
        </Link>
      );
    }
  },
  {
    accessor: 'status',
    Header: 'Status',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const status = rowData.row.original.status;
      return (
        <SoftBadge bg={statusBadgeMap[status] || 'secondary'} pill>
          {status === 'past_due'
            ? 'Payment Failed'
            : status?.charAt(0).toUpperCase() + status?.slice(1)}
        </SoftBadge>
      );
    }
  },
  {
    accessor: 'cancelled_at',
    Header: 'Cancelled',
    cellProps: { className: 'py-2 text-nowrap' },
    Cell: rowData => {
      const {
        status,
        cancelled_at,
        cancelled_by,
        cancelled_by_staff_name,
        cancellation_reason,
        cancellation_notes
      } = rowData.row.original;
      if (status !== 'cancelled') return '—';
      const who = cancelled_by
        ? cancelled_by_staff_name
          ? `By ${cancelled_by_staff_name} (staff)`
          : 'By donor'
        : 'In Stripe';
      // Compact: date only in the cell; who / reason / notes on hover
      return (
        <OverlayTrigger
          placement="top"
          // The table lives in a transformed scroll container, which throws
          // off absolute positioning - fixed keeps the tooltip on the icon
          popperConfig={{ strategy: 'fixed' }}
          overlay={
            <Tooltip>
              <div className="text-start">
                <div>{who}</div>
                {cancellation_reason && (
                  <div>Reason: {cancellation_reason}</div>
                )}
                {cancellation_notes && (
                  <div className="fst-italic">“{cancellation_notes}”</div>
                )}
              </div>
            </Tooltip>
          }
        >
          <span>
            {cancelled_at ? dayjs(cancelled_at).format('DD/MM/YY') : '—'}
            <FontAwesomeIcon
              icon="info-circle"
              className="ms-1 text-500 fs--2"
            />
          </span>
        </OverlayTrigger>
      );
    }
  },
  {
    accessor: 'payment_failed_at',
    Header: 'Failed',
    cellProps: { className: 'py-2 text-nowrap' },
    Cell: rowData => {
      const { status, payment_failed_at } = rowData.row.original;
      if (!payment_failed_at) return '—';
      return (
        <span
          className={status === 'past_due' ? 'text-danger' : 'text-600'}
          title={
            status === 'past_due'
              ? 'Payment failed - still unpaid'
              : 'Payment failed on this date, since resolved'
          }
        >
          {dayjs(payment_failed_at).format('DD/MM/YY')}
        </span>
      );
    }
  },
  {
    accessor: 'gift_aid_eligible',
    Header: 'Gift Aid',
    cellProps: { className: 'py-2' },
    Cell: rowData => (rowData.row.original.gift_aid_eligible ? 'Yes' : 'No')
  },
  {
    accessor: 'order_details_snapshot',
    Header: 'Items',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const items = rowData.row.original.order_details_snapshot;
      if (!items || !Array.isArray(items)) return '-';
      return (
        <ul className="list-unstyled mb-0">
          {items.map((item, i) => (
            <li key={i} className="fs--1 text-nowrap">
              {item.package_title || item.donation_type_name || 'Donation'}
              <span className="text-500"> · {formatMoney(item.amount)}</span>
            </li>
          ))}
        </ul>
      );
    }
  },
  {
    accessor: 'created_at',
    Header: 'Created',
    cellProps: { className: 'py-2 text-nowrap' },
    Cell: rowData => dayjs(rowData.row.original.created_at).format('DD/MM/YY')
  },
  {
    accessor: 'stripe_subscription_id',
    Header: 'Stripe ID',
    cellProps: { className: 'py-2 text-nowrap' },
    Cell: rowData => {
      const subId = rowData.row.original.stripe_subscription_id;
      if (!subId) return '-';
      // Full id on hover; the tail is what staff match against Stripe
      return (
        <span title={subId} className="fs--1 text-600">
          …{subId.slice(-8)}
        </span>
      );
    }
  },
  {
    accessor: 'actions',
    Header: 'Actions',
    cellProps: { className: 'py-2' },
    Cell: rowData => {
      const { status } = rowData.row.original;
      if (!canManage || (status !== 'active' && status !== 'past_due'))
        return null;
      return (
        <Button
          size="sm"
          variant="danger"
          disabled={isCancelling}
          onClick={() => onCancel(rowData.row.original)}
        >
          Cancel
        </Button>
      );
    }
  }
];

function RecurringDonations() {
  // Status filter kept for this tab, so coming back shows the same list
  const [savedStatus, setSavedStatus] = useSessionState(
    'filters:recurring-donations',
    ''
  );
  const [filters, setFilters] = useState(() =>
    savedStatus ? { status: savedStatus } : {}
  );
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const { recurringDonations, isLoading } = useRecurringDonations(filters);
  const { cancelSubscriptionAsync, isCancelling } = useCancelSubscription();
  const { isOrganizationAdmin, isAdmin, user } = useContext(AuthContext);

  const form = useForm({
    defaultValues: { status: savedStatus || '' }
  });

  const handleFilter = data => {
    setSavedStatus(data.status || '');
    setFilters({
      ...(data.status && { status: data.status })
    });
  };

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const handleCancel = subscription => {
    setSelectedSubscription(subscription);
    setSelectedSubscriptionId(subscription.stripe_subscription_id);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async (reason, notes) => {
    try {
      await cancelSubscriptionAsync({
        subscriptionId: selectedSubscriptionId,
        reason,
        notes,
        // For the note written to the donor's Notes tab
        donorId: selectedSubscription?.donor_id,
        branchId: selectedSubscription?.branch_id,
        amount: selectedSubscription?.amount
      });
      setShowCancelModal(false);
      setSelectedSubscriptionId(null);
    } catch (error) {
      // Error toast handled by the hook
    }
  };

  const staffName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(' ');

  const canManage = isOrganizationAdmin || isAdmin;
  const tableColumns = useMemo(
    () => columns(handleCancel, isCancelling, canManage),
    // handleCancel is recreated each render; the column defs only need the
    // latest values, and react-table must not see a new array every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCancelling, canManage]
  );

  const tableData = useMemo(() => {
    if (!recurringDonations?.length) return [];
    return recurringDonations.map(rd => ({
      ...rd,
      donor_name: rd.donor
        ? `${rd.donor.first_name} ${rd.donor.last_name}`
        : 'Unknown'
    }));
  }, [recurringDonations]);

  return (
    <div>
      <div className="card shadow-none border mb-4">
        <div className="card-body">
          <FormProvider
            methods={form}
            onSubmit={form.handleSubmit(handleFilter)}
          >
            <Row className="mb-2">
              <Col md={3}>
                <RHFSelect
                  name="status"
                  label="Status"
                  options={STATUS_OPTIONS}
                  noOptional={true}
                  isClearable={true}
                />
              </Col>
            </Row>
            <div className="d-flex gap-2 justify-content-end">
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={() => {
                  form.reset();
                  form.handleSubmit(handleFilter)();
                }}
              >
                Clear
              </Button>
              <Button size="sm" type="submit">
                Filter
              </Button>
            </div>
          </FormProvider>
        </div>
      </div>

      <CustomAdvanceTable
        data={tableData}
        columns={tableColumns}
        loading={isLoading}
        title="Recurring Donations"
      />

      <CancellationReasonModal
        show={showCancelModal}
        onHide={() => {
          setShowCancelModal(false);
          setSelectedSubscriptionId(null);
        }}
        handleConfirm={handleConfirmCancel}
        loading={isCancelling}
        title="Cancel Subscription"
        confirmText="Cancel Subscription"
        staffName={staffName}
      />
    </div>
  );
}

export default RecurringDonations;
